<template>
  <main class="login-shell">
    <div class="login-preview" aria-hidden="true">
      <div class="login-preview__bar">
        <div class="login-preview__brand"></div>
        <div class="login-preview__tabs">
          <span></span>
          <span></span>
        </div>
        <div class="login-preview__account"></div>
      </div>
      <div class="login-preview__body">
        <div class="login-preview__title"></div>
        <div class="login-preview__subtitle"></div>
        <div class="login-preview__grid">
          <div class="login-preview__drop">
            <div></div>
            <span></span>
            <small></small>
          </div>
          <div class="login-preview__side">
            <span></span>
            <span></span>
            <span></span>
            <button tabindex="-1"></button>
          </div>
        </div>
      </div>
    </div>

    <section class="login-card" aria-labelledby="login-title">
      <div class="login-card__brand">
        <img src="/images/branding/stratitude-logo-2026-black.png" alt="Stratitude" class="login-card__logo">
        <span aria-hidden="true"></span>
        <strong>Share</strong>
      </div>

      <div v-if="step === 'email'" class="login-card__content">
        <div class="login-card__icon" aria-hidden="true">
          <svg class="ui-icon" viewBox="0 0 24 24">
            <path d="M7 10V8a5 5 0 0 1 10 0v2m-11 0h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"/>
          </svg>
        </div>
        <h1 id="login-title">Secure file sharing for the Stratitude team.</h1>
        <p class="login-card__intro">
          Enter your work email and we’ll send you a six-digit sign-in code.
        </p>

        <form @submit.prevent="requestOtp">
          <div class="field-group">
            <label for="login-email">Work email address</label>
            <input
              id="login-email"
              v-model.trim="email"
              class="form-control-ui"
              type="email"
              autocomplete="email"
              inputmode="email"
              placeholder="name@stratitude.co.za"
              :disabled="loading"
              required
            >
            <span class="field-help">Only @stratitude.co.za email addresses can sign in.</span>
          </div>

          <button class="btn-ui btn-ui--login btn-ui--block" type="submit" :disabled="loading">
            <span>{{ loading ? 'Sending code…' : 'Send sign-in code' }}</span>
            <svg v-if="!loading" class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>
        </form>
      </div>

      <div v-else class="login-card__content">
        <button class="text-button login-card__back" type="button" :disabled="loading" @click="backToEmail">
          <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          <span>Change email</span>
        </button>

        <div class="login-card__icon" aria-hidden="true">
          <svg class="ui-icon" viewBox="0 0 24 24">
            <path d="M4 7.5 12 13l8-5.5M5.5 5h13A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-11A1.5 1.5 0 0 1 5.5 5Z"/>
          </svg>
        </div>
        <h1 id="login-title">Check your inbox.</h1>
        <p class="login-card__intro">
          Enter the code sent to <strong>{{ email }}</strong>.
        </p>

        <form @submit.prevent="submitOtp">
          <fieldset class="otp-fieldset">
            <legend>Six-digit sign-in code</legend>
            <div class="otp-inputs" @paste="handlePaste">
              <input
                v-for="(digit, index) in otpDigits"
                :key="index"
                ref="otpInputs"
                :value="digit"
                class="otp-input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                :autocomplete="index === 0 ? 'one-time-code' : 'off'"
                :aria-label="'Code digit ' + (index + 1)"
                :disabled="loading"
                @input="handleOtpInput(index, $event)"
                @keydown="handleOtpKeydown(index, $event)"
              >
            </div>
          </fieldset>

          <button class="btn-ui btn-ui--login btn-ui--block" type="submit" :disabled="loading || otp.length !== 6">
            {{ loading ? 'Verifying…' : 'Verify and continue' }}
          </button>
        </form>

        <button class="text-button login-card__resend" type="button" :disabled="loading || resendSeconds > 0" @click="resendOtp">
          {{ resendSeconds > 0 ? 'Resend available in ' + resendSeconds + 's' : 'Resend sign-in code' }}
        </button>
      </div>

      <div v-if="error" class="notice notice--error login-card__notice" role="alert">
        <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 8v5m0 3h.01M10.3 4.3 2.7 17.5A1.7 1.7 0 0 0 4.2 20h15.6a1.7 1.7 0 0 0 1.5-2.5L13.7 4.3a2 2 0 0 0-3.4 0Z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <div v-if="success" class="notice notice--success login-card__notice" role="status">
        <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m5 12 4 4L19 6"/>
        </svg>
        <span>{{ success }}</span>
      </div>

      <footer class="login-card__footer">
        <a href="https://www.stratitude.co.za/policies" target="_blank" rel="noopener noreferrer">Privacy policy</a>
        <span aria-hidden="true">·</span>
        <a href="https://www.stratitude.co.za/" target="_blank" rel="noopener noreferrer">Stratitude website</a>
      </footer>
    </section>
  </main>
</template>

<script>
import { STAFF_DOMAIN } from '../config'
import { sendOtp, verifyOtp } from '../services/shareApi'
import { readLastEmail, saveAuth } from '../utils/auth'

export default {
  name: 'LoginScreen',

  data: function () {
    return {
      step: 'email',
      email: readLastEmail(),
      otpDigits: ['', '', '', '', '', ''],
      loading: false,
      error: '',
      success: '',
      resendSeconds: 0,
      resendTimer: null
    }
  },

  computed: {
    otp: function () {
      return this.otpDigits.join('')
    }
  },

  beforeDestroy: function () {
    this.clearResendTimer()
  },

  methods: {
    isValidStaffEmail: function (email) {
      var normalized = String(email || '').trim().toLowerCase()
      var validFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
      return validFormat && normalized.endsWith(STAFF_DOMAIN)
    },

    apiMessage: function (response, fallback) {
      return response && response.data && response.data.message
        ? response.data.message
        : fallback
    },

    requestOtp: async function () {
      this.error = ''
      this.success = ''
      this.email = String(this.email || '').trim().toLowerCase()

      if (!this.isValidStaffEmail(this.email)) {
        this.error = 'Please use a valid @stratitude.co.za email address.'
        return
      }

      this.loading = true

      try {
        var response = await sendOtp(this.email)

        if (!response.data || !response.data.success) {
          this.error = this.apiMessage(response, 'Could not send the sign-in code.')
          return
        }

        this.step = 'otp'
        this.otpDigits = ['', '', '', '', '', '']
        this.success = response.data.message || 'Your sign-in code is on its way.'
        this.startResendCooldown(60)

        this.$nextTick(function () {
          this.focusOtpInput(0)
        })
      } catch (error) {
        this.error = this.apiMessage(error.response, 'Could not send the sign-in code.')
      } finally {
        this.loading = false
      }
    },

    resendOtp: async function () {
      if (this.resendSeconds > 0 || this.loading) {
        return
      }

      await this.requestOtp()
    },

    handleOtpInput: function (index, event) {
      var incoming = String(event.target.value || '').replace(/\D/g, '')

      // Mobile/desktop one-time-code autofill can place the full code into the
      // first input instead of firing a paste event.
      if (incoming.length > 1) {
        var available = incoming.slice(0, 6 - index)
        for (var offset = 0; offset < available.length; offset += 1) {
          this.$set(this.otpDigits, index + offset, available[offset])
        }
        event.target.value = available[0]
        this.$nextTick(function () {
          this.focusOtpInput(Math.min(index + available.length, 6) - 1)
        })
        return
      }

      var value = incoming.slice(-1)
      this.$set(this.otpDigits, index, value)
      event.target.value = value

      if (value && index < this.otpDigits.length - 1) {
        this.focusOtpInput(index + 1)
      }
    },

    handleOtpKeydown: function (index, event) {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        this.$set(this.otpDigits, index - 1, '')
        this.focusOtpInput(index - 1)
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusOtpInput(index - 1)
      }

      if (event.key === 'ArrowRight' && index < this.otpDigits.length - 1) {
        event.preventDefault()
        this.focusOtpInput(index + 1)
      }
    },

    handlePaste: function (event) {
      var digits = String(event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)

      if (!digits) {
        return
      }

      event.preventDefault()

      for (var index = 0; index < 6; index += 1) {
        this.$set(this.otpDigits, index, digits[index] || '')
      }

      this.$nextTick(function () {
        this.focusOtpInput(Math.min(digits.length, 6) - 1)
      })
    },

    focusOtpInput: function (index) {
      var inputs = this.$refs.otpInputs || []
      if (inputs[index]) {
        inputs[index].focus()
        inputs[index].select()
      }
    },

    submitOtp: async function () {
      this.error = ''
      this.success = ''

      if (this.otp.length !== 6) {
        this.error = 'Please enter the complete six-digit code.'
        return
      }

      this.loading = true

      try {
        var response = await verifyOtp(this.email, this.otp)

        if (!response.data || !response.data.success || !response.data.token) {
          this.error = this.apiMessage(response, 'Could not verify the sign-in code.')
          return
        }

        var verifiedEmail = response.data.user && response.data.user.email
          ? response.data.user.email
          : this.email
        var parsedExpiry = response.data.expires_at ? Date.parse(response.data.expires_at) : NaN
        var expiry = Number.isNaN(parsedExpiry)
          ? Date.now() + (8 * 60 * 60 * 1000)
          : parsedExpiry
        var auth = saveAuth(response.data.token, verifiedEmail, expiry)

        this.clearResendTimer()
        this.$emit('authenticated', auth)
      } catch (error) {
        this.error = this.apiMessage(error.response, 'Could not verify the sign-in code.')
      } finally {
        this.loading = false
      }
    },

    backToEmail: function () {
      this.step = 'email'
      this.otpDigits = ['', '', '', '', '', '']
      this.error = ''
      this.success = ''
      this.clearResendTimer()
    },

    startResendCooldown: function (seconds) {
      this.clearResendTimer()
      this.resendSeconds = seconds
      this.resendTimer = window.setInterval(function () {
        if (this.resendSeconds <= 1) {
          this.clearResendTimer()
          return
        }

        this.resendSeconds -= 1
      }.bind(this), 1000)
    },

    clearResendTimer: function () {
      if (this.resendTimer) {
        window.clearInterval(this.resendTimer)
        this.resendTimer = null
      }
      this.resendSeconds = 0
    }
  }
}
</script>
